/**
 * What a coach reads when a page's data did not load.
 *
 * The dashboard printed the server's body under "Could not load client" — for
 * a 500 that was whatever the function threw, "boom" in the e2e mock and a
 * stack fragment in production. That names nothing the reader can act on.
 * This says what happened in their terms and what to do; the status code
 * survives as a small detail for whoever they forward the screenshot to.
 */
export interface LoadErrorCopy {
  /** One or two sentences: what happened, and the way out. */
  message: string;
  /** e.g. "Error 500", for support. Absent when there was no response. */
  detail?: string;
}

const statusOf = (e: unknown): number | undefined => {
  const s = (e as { status?: unknown } | null)?.status;
  return typeof s === 'number' ? s : undefined;
};

export const describeLoadError = (e: unknown): LoadErrorCopy => {
  const status = statusOf(e);
  const detail = status !== undefined ? `Error ${status}` : undefined;
  if (status === 404) {
    return { message: 'No client with that id on this account.', detail };
  }
  if (status === 401 || status === 403) {
    return {
      message: 'Your session does not have access to this. Sign in again, then try again.',
      detail,
    };
  }
  if (status !== undefined && status >= 500) {
    return {
      message: 'Sizewall had a problem on its side. Nothing was changed; try again in a moment.',
      detail,
    };
  }
  if (status !== undefined) {
    return { message: 'The request was refused. Try again, and if it keeps failing, contact support.', detail };
  }
  return { message: 'The request did not come back. Check your connection and try again.' };
};
